/**
 * §21.3 GastroGest — Margen por producto y por categoría (DATO DEL DUEÑO). Solo se
 * monta para roles con financials:view (gateado en Dashboard + en el API: 403). El
 * ingreso es NETO (sin IVA); el costo sale de la variante (V36) cuando aplica. Reusa
 * DatePeriodFilter y el cliente api(); se hidrata de GET /pos/insights/margin.
 */
import { useEffect, useMemo, useState } from 'react';
import { TrendingUp, Layers, Package, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { DatePeriodFilter, periodToQuery, periodLabel, defaultPeriod, type PeriodValue } from '../components/DatePeriodFilter';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (v: any) => Number(v ?? 0).toLocaleString('es-MX');
const pct = (v: any) => `${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

interface MarginItem {
  productId: string | null; variantId: string | null; serviceId: string | null;
  name: string; kind: 'product' | 'service'; category: string;
  units: number; revenue: number; cost: number; margin: number; marginPct: number; costMissing: boolean;
}
interface MarginCategory {
  category: string; units: number; revenue: number; cost: number; margin: number; marginPct: number; costMissing: boolean;
}
interface MarginData {
  from: string; to: string;
  totals: { units: number; revenue: number; cost: number; margin: number; marginPct: number };
  byProduct: MarginItem[];
  byCategory: MarginCategory[];
}

type SortKey = 'units' | 'revenue' | 'cost' | 'margin' | 'marginPct';

function Tile({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`font-serif text-2xl tabular-nums ${accent ?? 'text-slate-900'}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

const marginTone = (p: number) => (p >= 50 ? 'text-emerald-700' : p >= 25 ? 'text-slate-800' : p > 0 ? 'text-amber-600' : 'text-rose-600');

export function MarginView({ sourceModule = 'gastro' }: { sourceModule?: 'gastro' | 'boutique' | 'gym' }) {
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  const [data, setData] = useState<MarginData | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<'product' | 'category'>('product');
  const [sortKey, setSortKey] = useState<SortKey>('margin');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams(periodToQuery(period));
    qs.set('sourceModule', sourceModule);
    api(`/pos/insights/margin?${qs.toString()}`)
      .then((r) => { if (!cancelled) setData(r as MarginData); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, sourceModule]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(k); setSortDir('desc'); }
  };

  const rows = useMemo(() => {
    const base: Array<MarginItem | MarginCategory> = groupBy === 'product' ? (data?.byProduct ?? []) : (data?.byCategory ?? []);
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...base].sort((a, b) => ((a as any)[sortKey] - (b as any)[sortKey]) * dir);
  }, [data, groupBy, sortKey, sortDir]);

  const t = data?.totals;
  const anyCostMissing = !!data?.byProduct?.some((p) => p.costMissing);
  const arrow = (k: SortKey) => (k === sortKey ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '');

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Margen por producto</h2>
          <p className="text-sm text-slate-500">Ingreso (sin IVA), costo y margen — {periodLabel(period)}.</p>
        </div>
        <DatePeriodFilter value={period} onChange={setPeriod} />
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : !data ? (
        <p className="text-sm text-slate-400">Sin datos para el período.</p>
      ) : (
        <>
          {/* KPIs totales */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Ingreso (sin IVA)" value={mxn(t?.revenue)} hint={`${int(t?.units)} unidades`} />
            <Tile label="Costo" value={mxn(t?.cost)} />
            <Tile label="Margen" value={mxn(t?.margin)} accent={marginTone(t?.marginPct ?? 0)} />
            <Tile label="Margen %" value={pct(t?.marginPct)} accent={marginTone(t?.marginPct ?? 0)} />
          </div>

          {anyCostMissing && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Algunos ítems no tienen <strong>costo</strong> capturado en el catálogo: su margen aparece como 100% (costo $0). Captura el costo del producto/variante para que el margen sea real (marcados con <strong>!</strong>).</span>
            </div>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-serif text-lg text-slate-900">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                {groupBy === 'product' ? 'Por producto' : 'Por categoría'}
              </h3>
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[11px]">
                <button onClick={() => setGroupBy('product')} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 ${groupBy === 'product' ? 'bg-slate-900 text-white' : 'text-slate-500'}`}><Package className="h-3 w-3" />Producto</button>
                <button onClick={() => setGroupBy('category')} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 ${groupBy === 'category' ? 'bg-slate-900 text-white' : 'text-slate-500'}`}><Layers className="h-3 w-3" />Categoría</button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2">{groupBy === 'product' ? 'Ítem' : 'Categoría'}</th>
                    {groupBy === 'product' && <th className="px-3 py-2">Categoría</th>}
                    <th className="cursor-pointer select-none px-3 py-2 text-right" onClick={() => toggleSort('units')}>Unid.{arrow('units')}</th>
                    <th className="cursor-pointer select-none px-3 py-2 text-right" onClick={() => toggleSort('revenue')}>Ingreso{arrow('revenue')}</th>
                    <th className="cursor-pointer select-none px-3 py-2 text-right" onClick={() => toggleSort('cost')}>Costo{arrow('cost')}</th>
                    <th className="cursor-pointer select-none px-3 py-2 text-right" onClick={() => toggleSort('margin')}>Margen{arrow('margin')}</th>
                    <th className="cursor-pointer select-none px-3 py-2 text-right" onClick={() => toggleSort('marginPct')}>Margen %{arrow('marginPct')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.length === 0 && <tr><td colSpan={groupBy === 'product' ? 7 : 6} className="px-3 py-6 text-center text-slate-400">Sin ventas en el período.</td></tr>}
                  {rows.map((r, i) => {
                    const item = r as MarginItem;
                    return (
                      <tr key={(item.variantId ?? item.productId ?? item.serviceId ?? '') + (r as any).category + i}>
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {('name' in r) ? (r as MarginItem).name : (r as MarginCategory).category}
                          {('kind' in r) && (
                            <span className={`ml-2 rounded px-1 py-0.5 text-[9px] uppercase ${item.kind === 'service' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-700'}`}>{item.kind === 'service' ? 'serv' : 'prod'}</span>
                          )}
                          {r.costMissing && <span title="Sin costo capturado" className="ml-1 font-bold text-amber-500">!</span>}
                        </td>
                        {groupBy === 'product' && <td className="px-3 py-2 text-slate-500">{(r as MarginItem).category}</td>}
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{int(r.units)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{mxn(r.revenue)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{mxn(r.cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{mxn(r.margin)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${marginTone(r.marginPct)}`}>{pct(r.marginPct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
